import ufeApi from 'services/api/ufeApi';
import { gqlClient } from 'services/api/gql/gqlClient';
import { SubmitReturnReship } from 'constants/gql/mutations/checkout/submitReturnReship.gql';

// https://confluence.sephora.com/wiki/pages/viewpage.action?spaceKey=ILLUMINATE&title=Submit+Return+Reship+Order

/**
 * Submits a return or reship order for the given original and replacement order IDs.
 * Routes to OIS GraphQL (SubmitReturnReship) or legacy OXS REST based on oisMigration feature flags.
 *
 * @param {Object} options - The submit return/reship payload
 * @param {string} options.originalOrderId - The original order ID being returned
 * @param {string} options.replacementOrderId - The replacement order ID
 * @returns {Promise<Object>} The submit return/reship response
 * @throws {Error} Throws with errorCode=responseCode and errorMessages for OIS non-SUCCESS responses
 * @throws {Object} Throws raw error object for OXS errors when errorCode is present
 */
const confirmReturnOrder = async ({ originalOrderId, replacementOrderId } = {}) => {
    const oxsUrl = '/api/selfReturn/confirmIRorNCR';

    const globalEnabled = Sephora?.configurationSettings?.oisMigration?.global?.isEnabled;
    const returnsEnabled = Sephora?.configurationSettings?.oisMigration?.returns?.isEnabled;

    if (globalEnabled && returnsEnabled) {
        const data = await gqlClient.query({
            operationName: 'SubmitReturnReship',
            query: SubmitReturnReship,
            variables: {
                input: {
                    originalOrderId,
                    replacementOrderId
                }
            }
        });

        const result = data?.submitReturnNReshipOrder;

        if (!result) {
            throw new Error('Submit return/reship order: unexpected response shape');
        }

        if (result.responseCode && result.responseCode !== 'SUCCESS') {
            const error = new Error(result.message || 'Submit return/reship order failed');
            error.errorCode = result.responseCode;
            error.errorMessages = result.errors?.length ? result.errors.map(e => e.message) : result.message ? [result.message] : [];
            throw error;
        }

        return { ...result, responseStatus: 200 };
    }

    const data = await ufeApi.makeRequest(
        oxsUrl,
        {
            method: 'POST',
            body: JSON.stringify({ originalOrderId, replacementOrderId })
        },
        { skipLoginPrompt: true }
    );

    if (data?.errorCode) {
        throw data;
    }

    return data;
};

export default confirmReturnOrder;
